import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import ImageUploader from "@/components/admin/ImageUploader";
import LocalizedInput from "@/components/admin/LocalizedInput";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, ArrowLeft, Loader2, Eye } from "lucide-react";

const EMPTY_LOC = { de: "", en: "", es: "" };

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);

const AdminBlogEditor = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [previewLocale, setPreviewLocale] = useState("de");
  const [form, setForm] = useState({
    slug: "",
    cover_image: "",
    tags: "",
    read_minutes: 5,
    title: { ...EMPTY_LOC },
    excerpt: { ...EMPTY_LOC },
    content: { ...EMPTY_LOC },
    published: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    api
      .get("/admin/blog/posts")
      .then(({ data }) => {
        const post = data.find((p) => p.id === id);
        if (!post) {
          toast.error("Beitrag nicht gefunden");
          navigate("/admin/blog");
          return;
        }
        setForm({
          slug: post.slug,
          cover_image: post.cover_image || "",
          tags: (post.tags || []).join(", "),
          read_minutes: post.read_minutes,
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          published: post.published,
        });
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const save = async () => {
    if (!form.slug || !form.title.de || !form.excerpt.de || !form.content.de) {
      toast.error("Slug, Titel, Excerpt und Inhalt (DE) sind Pflicht.");
      return;
    }
    setSaving(true);
    const fillFallback = (obj) => ({
      de: obj.de,
      en: obj.en || obj.de,
      es: obj.es || obj.de,
    });
    const payload = {
      slug: slugify(form.slug),
      cover_image: form.cover_image || null,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      read_minutes: Number(form.read_minutes) || 5,
      title: fillFallback(form.title),
      excerpt: fillFallback(form.excerpt),
      content: fillFallback(form.content),
      published: form.published,
    };
    try {
      if (isEdit) {
        await api.put(`/admin/blog/posts/${id}`, payload);
        toast.success("Beitrag aktualisiert");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Beitrag erstellt");
        navigate("/admin/blog");
        return;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      title={isEdit ? "Beitrag bearbeiten" : "Neuer Beitrag"}
      subtitle={isEdit ? `Slug: /${form.slug}` : "Neuer Magazin-Artikel (DE Pflicht, EN/ES optional)"}
      actions={
        <>
          <button
            onClick={() => navigate("/admin/blog")}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
            data-testid="blog-editor-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
          {isEdit && (
            <a
              href={`/blog/${form.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
            >
              <Eye className="w-4 h-4" />
              Live ansehen
            </a>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="blog-editor-save"
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm font-medium hover:bg-black disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
        </>
      }
    >
      {loading ? (
        <div className="py-24 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Meta */}
          <div className="rounded-3xl bg-white border border-neutral-200 p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Slug</div>
                <Input
                  data-testid="blog-editor-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  Tags (Komma-getrennt)
                </div>
                <Input
                  data-testid="blog-editor-tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  Lesezeit (Min)
                </div>
                <Input
                  type="number"
                  min={1}
                  data-testid="blog-editor-read"
                  value={form.read_minutes}
                  onChange={(e) => setForm({ ...form, read_minutes: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <ImageUploader
              value={form.cover_image}
              onChange={(url) => setForm({ ...form, cover_image: url })}
              label="Cover-Bild"
              testId="blog-editor-cover"
            />

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="blog-editor-published"
                className="rounded"
              />
              Veröffentlicht (Live)
            </label>
          </div>

          {/* Title + Excerpt */}
          <div className="rounded-3xl bg-white border border-neutral-200 p-6 space-y-5">
            <LocalizedInput
              label="Titel"
              value={form.title}
              onChange={(v) => setForm({ ...form, title: v })}
              testId="blog-editor-title"
              required
            />
            <LocalizedInput
              label="Excerpt"
              value={form.excerpt}
              onChange={(v) => setForm({ ...form, excerpt: v })}
              multi
              rows={3}
              testId="blog-editor-excerpt"
              required
            />
          </div>

          {/* Content editor + preview */}
          <div className="rounded-3xl bg-white border border-neutral-200 p-6">
            <Tabs value={previewLocale} onValueChange={setPreviewLocale}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  Inhalt (Markdown) · Live-Vorschau
                </div>
                <TabsList>
                  <TabsTrigger value="de" data-testid="blog-editor-tab-de">DE</TabsTrigger>
                  <TabsTrigger value="en" data-testid="blog-editor-tab-en">EN</TabsTrigger>
                  <TabsTrigger value="es" data-testid="blog-editor-tab-es">ES</TabsTrigger>
                </TabsList>
              </div>

              {["de", "en", "es"].map((l) => (
                <TabsContent key={l} value={l} className="mt-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <textarea
                      value={form.content[l] || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          content: { ...form.content, [l]: e.target.value },
                        })
                      }
                      data-testid={`blog-editor-content-${l}`}
                      rows={22}
                      placeholder={`Markdown-Inhalt (${l.toUpperCase()})…`}
                      className="w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 font-mono text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-sky-300 resize-y"
                    />
                    <div
                      data-testid={`blog-editor-preview-${l}`}
                      className="rounded-2xl border border-neutral-200 bg-white px-5 py-4 overflow-auto max-h-[640px]
                        prose prose-neutral prose-sm max-w-none
                        prose-headings:font-display prose-headings:font-medium prose-headings:tracking-tight
                        prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2
                        prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2
                        prose-p:text-[14px] prose-p:leading-relaxed
                        prose-strong:text-neutral-950
                        prose-a:text-sky-700 prose-a:no-underline
                        prose-blockquote:border-l-sky-500 prose-blockquote:bg-sky-50/60 prose-blockquote:px-4 prose-blockquote:py-1 prose-blockquote:not-italic"
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {form.content[l] || "_Kein Inhalt_"}
                      </ReactMarkdown>
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminBlogEditor;
